import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { buscar, buscarEntre, lecturaConDesactivados, lecturaDeCodigo, unidadesConCodigo } from '../lib/catalogo.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { usePuede } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { CualEs, type Hallado } from './CualEs.js';
import { EnsenarCodigo } from './EnsenarCodigo.js';
import { BotonEscanear } from './Escaner.js';
import { EscanerProductos, type OpcionesEscaner } from './EscanerProductos.js';
import { Chip, Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';
import { Modal } from './Modal.js';
import { SugerenciaAbierta } from './SugerenciaAbierta.js';

/**
 * The products the list under the box draws at a time, thirty more as it is scrolled near its foot or
 * the arrows go past the last: it drew eight, and a ninth «galleta» was out of reach but by typing more
 * (2026-10-12, after the owner's «it abruptly stops»).
 */
const PRODUCTOS_A_LA_VEZ = 30;

/**
 * Type-to-find product picker, with the camera beside it.
 *
 * Search-first rather than barcode-first: a good share of pulpería stock — bulk rice,
 * repackaged goods, anything sold by the pound — has no usable barcode at all, so typing is
 * never the fallback, it is half the job. The camera button sits beside the box on any device
 * that has a camera, and a USB scanner needs nothing at all: it types the code and presses
 * Enter, and a typed code finds its product first — with the presentation, when the code is
 * one presentation's own.
 *
 * What a scanner types gets the answers the camera gives. A code on one presentation says which
 * in the list (the case, not «unidad»); a code on two products is asked about in «¿Cuál es?»
 * (`CualEs`), the window the camera asks it in, rather than handed to whichever sorts first; and a
 * code nobody has can be given to its product from here, by whoever may edit products, the way the
 * camera teaches one.
 *
 * Where a screen can make a product (`onNuevo`, receiving goods), a name typed gets a last row
 * in the list that makes one under that name: what is not in the catalogue yet is added without
 * leaving the screen, and a code is offered the same through `escaner.desconocido`.
 *
 * Where a scanner is a way in (the till, receiving goods, counts, transfers, repacking, orders),
 * `lectorUsb` brings this box whatever a USB scanner types, wherever the cursor is, and
 * `preguntarEnHoja` asks about a code nobody has in a sheet that has to be answered, as the camera
 * does, instead of in a line under the box that is easy to miss. Not for a box inside a sheet, which
 * would take the sheet it is in for one in the way (`lib/lector-usb.ts`) and drop every scan.
 *
 * `inactivos` finds the products taken off sale too, each marked «Desactivado»: a count's, for what
 * is still on the shelf after it stopped being sold (the owner, 2026-10-18). A code is one of those
 * only when nothing on sale answers to it (`lecturaConDesactivados`).
 */
export function BuscadorProducto({
  onElegir,
  etiqueta,
  autoFocus = false,
  escaner = {},
  campoRef,
  onNuevo,
  lectorUsb = false,
  preguntarEnHoja = false,
  listaId,
  inactivos = false,
}: {
  onElegir: (p: Producto, unidad?: UnidadVenta) => void;
  /** Omitted falls back to "Producto"; pass an empty string for no label at all. */
  etiqueta?: string;
  autoFocus?: boolean;
  /** How the camera behaves here; `false` for no camera button at all. */
  escaner?: OpcionesEscaner | false;
  /** The box itself, for a screen that sends what is typed on it here (the till). */
  campoRef?: Ref<HTMLInputElement>;
  /** A new product under the name typed, offered at the foot of the list. Only where one can be made. */
  onNuevo?: (nombre: string) => void;
  /** Whatever a USB scanner types on the screen is read here, wherever the cursor is (`useLectorUsb`). */
  lectorUsb?: boolean;
  /** A code nobody has is asked about in a sheet, not said under the box. */
  preguntarEnHoja?: boolean;
  /** The prices «¿Cuál es?» shows: the sale's list, or the order's. The shop's own when not given. */
  listaId?: string;
  /** The products taken off sale are found too, and marked: what a count needs. */
  inactivos?: boolean;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const puedeEnsenar = usePuede('editar_productos');
  const [consulta, setConsulta] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [cuantos, setCuantos] = useState(PRODUCTOS_A_LA_VEZ);
  const lista = useRef<HTMLUListElement | null>(null);
  /** Moved through the list with the arrows: Enter is then a choice, not a scan. */
  const [movido, setMovido] = useState(false);
  const [escaneando, setEscaneando] = useState(false);
  /** A code typed and entered that the catalogue does not have: what a USB scanner read. */
  const [desconocido, setDesconocido] = useState<string | null>(null);
  /** That code, being given to its product. */
  const [ensenando, setEnsenando] = useState<string | null>(null);
  /** That code, asked about in a sheet (`preguntarEnHoja`). */
  const [pregunta, setPregunta] = useState<string | null>(null);
  /** A code entered that more than one product answers to, while «¿Cuál es?» asks which. */
  const [cual, setCual] = useState<{ codigo: string; hallados: Hallado[] } | null>(null);
  /** Where the cursor was when «¿Cuál es?» opened, for it to go back to. */
  const cursorAntes = useRef<HTMLElement | null>(null);
  const cerrarTimer = useRef<number | undefined>(undefined);
  /** The box, for the scanner to send what it reads to, and for whoever passed `campoRef` too. */
  const caja = useRef<HTMLInputElement | null>(null);
  const refCaja = useCallback(
    (el: HTMLInputElement | null) => {
      caja.current = el;
      if (typeof campoRef === 'function') campoRef(el);
      else if (campoRef) campoRef.current = el;
    },
    [campoRef],
  );

  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  // Not `hallados`: Enter's own, below, reads a code's products under that name.
  const encontrados = useMemo(
    () => (consulta.trim() ? (inactivos ? buscarEntre : buscar)(productos, consulta, Infinity, nombre, unidades) : []),
    [productos, consulta, nombre, unidades, inactivos],
  );
  /** What a code rings up here: off sale too where they are found (`inactivos`). */
  const leerLaLectura = (codigo: string) =>
    inactivos ? lecturaConDesactivados(productos, unidades, codigo) : lecturaDeCodigo(productos, unidades, codigo);
  const resultados = encontrados.slice(0, cuantos);
  /** The presentations whose own code this is: the case's code names the case in the list. */
  const deLaConsulta = useMemo(() => unidadesConCodigo(unidades, consulta), [unidades, consulta]);
  /**
   * The row that makes a product of what was typed, after the results. Not for digits alone,
   * which are a code a scanner is still typing: one nobody has gets its own answer on Enter.
   */
  const nuevo = onNuevo && consulta.trim() && !/^\d+$/.test(consulta.trim()) ? consulta.trim() : null;
  /** Rows the arrows move through: the results, and that one. */
  const filas = resultados.length + (nuevo ? 1 : 0);
  // The row the arrows reached stays in sight, the list scrolled and never the page, above the row
  // that makes one, which keeps to the list's foot.
  useEffect(() => {
    const caja = lista.current;
    const fila = caja?.children[resaltado];
    if (!abierto || !movido || !caja || !(fila instanceof HTMLElement)) return;
    const pie = fila.classList.contains('sticky') ? 0 : (caja.querySelector<HTMLElement>(':scope > li.sticky')?.offsetHeight ?? 0);
    if (fila.offsetTop < caja.scrollTop) caja.scrollTop = fila.offsetTop;
    else if (fila.offsetTop + fila.offsetHeight > caja.scrollTop + caja.clientHeight - pie)
      caja.scrollTop = fila.offsetTop + fila.offsetHeight - caja.clientHeight + pie;
  }, [abierto, movido, resaltado]);

  function elegir(p: Producto) {
    onElegir(p, deLaConsulta.find((u) => u.productoId === p.id));
    setConsulta('');
    setAbierto(false);
    setResaltado(0);
  }

  /** A code nobody answers to: under the box, or asked about in a sheet. */
  function noEsta(codigo: string) {
    if (preguntarEnHoja) setPregunta(codigo);
    else setDesconocido(codigo);
  }

  /**
   * A code on more than one product: which is in the hand, asked in a window that has to be answered.
   * It used to ask in the list under the box, a line easy to miss with the eyes on the goods.
   */
  function preguntarCual(codigo: string, hallados: Hallado[]) {
    window.clearTimeout(cerrarTimer.current);
    setAbierto(false);
    cursorAntes.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCual({ codigo, hallados });
  }

  /** «¿Cuál es?» answered, either way: the cursor goes back where it was before it opened. */
  function soltarCual() {
    setCual(null);
    const antes = cursorAntes.current;
    cursorAntes.current = null;
    if (antes?.isConnected && antes !== document.body) antes.focus();
  }

  /** What a USB scanner read, wherever it typed it: as Enter in the box would have, codes only. */
  function leerCodigo(codigo: string) {
    const hallados = leerLaLectura(codigo);
    setDesconocido(null);
    if (hallados.length === 1) onElegir(hallados[0]!.producto, hallados[0]!.unidad);
    else if (hallados.length > 1) preguntarCual(codigo, hallados);
    else noEsta(codigo);
  }
  useLectorUsb(caja, leerCodigo, { activo: lectorUsb });

  function crear(nombre: string) {
    onNuevo?.(nombre);
    setConsulta('');
    setAbierto(false);
    setResaltado(0);
  }

  return (
    <div className="relative">
      <div className="flex items-end gap-2">
        <label className="block min-w-0 flex-1">
          <span className="etiqueta">{etiqueta ?? t('pos.producto')}</span>
          <input
            ref={refCaja}
            // A box made for codes: a scan typed into it stays here, whichever box reads the scanner.
            data-codigo
            className="campo"
            value={consulta}
            autoFocus={autoFocus}
            autoComplete="off"
            placeholder={t('buscador.placeholder')}
            onChange={(e) => {
              setConsulta(e.target.value);
              setAbierto(true);
              setResaltado(0);
              setCuantos(PRODUCTOS_A_LA_VEZ);
              setMovido(false);
              setDesconocido(null);
            }}
            onFocus={() => {
              // Back in the box before the last blur's close ran: that close is stale. Left to
              // run, it shut the list on whatever was typed in the next 150 ms, which is all of
              // it when a scanner types, or somebody quick right after tapping a line's +.
              window.clearTimeout(cerrarTimer.current);
              setAbierto(true);
            }}
            onBlur={() => {
              // Delay so a click on an option lands before the list unmounts.
              cerrarTimer.current = window.setTimeout(() => setAbierto(false), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMovido(true);
                // Past the last drawn, with more found: the next ones, before the row that makes one.
                if (resaltado + 1 >= resultados.length && cuantos < encontrados.length) {
                  setCuantos(cuantos + PRODUCTOS_A_LA_VEZ);
                  setResaltado(resaltado + 1);
                } else setResaltado((r) => Math.min(r + 1, filas - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMovido(true);
                setResaltado((r) => Math.max(r - 1, 0));
              }
              const hallados = e.key === 'Enter' && !movido ? leerLaLectura(consulta) : [];
              if (hallados.length > 1) {
                /*
                 * A code on two products, typed and entered, or sent by a scanner this box does not
                 * read for the screen. Enter took whichever sorted first, where the camera asks which
                 * is in the hand; now it asks too, in the same window, and the box is emptied for the
                 * next one.
                 */
                e.preventDefault();
                preguntarCual(consulta.trim(), hallados);
                setConsulta('');
              } else if (e.key === 'Enter' && resultados[resaltado]) {
                e.preventDefault();
                window.clearTimeout(cerrarTimer.current);
                elegir(resultados[resaltado]);
              } else if (e.key === 'Enter' && /^\d{6,14}$/.test(consulta.trim())) {
                /*
                 * A code nothing answers to, as a USB scanner types it. It used to do nothing and
                 * leave the digits in the box, so the next scan was appended to them and that
                 * product did not ring up either. Said, as the camera says it, and the box emptied
                 * for the next one.
                 */
                e.preventDefault();
                noEsta(consulta.trim());
                setConsulta('');
                setAbierto(false);
              } else if (e.key === 'Enter' && nuevo && resaltado >= resultados.length) {
                // The last row, reached with the arrows, or the only one when nothing is called that.
                e.preventDefault();
                window.clearTimeout(cerrarTimer.current);
                crear(nuevo);
              }
              if (e.key === 'Escape') setAbierto(false);
            }}
          />
        </label>
        {escaner !== false && <BotonEscanear onClick={() => setEscaneando(true)} />}
      </div>

      {desconocido && (
        <div role="status" className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-amber-200">
          <p className="min-w-0">{t('buscador.codigoDesconocido', { codigo: desconocido })}</p>
          {/* Where a scanner is expected: the product it belongs to, then whatever else this screen offers. */}
          {escaner !== false && puedeEnsenar && (
            <button type="button" className="btn-secundario h-11 min-h-0 px-4 text-sm" onClick={() => setEnsenando(desconocido)}>
              <Ico icono="enlazar" className="size-4" />
              {t('escaner.esDeUnProducto')}
            </button>
          )}
          {escaner !== false && escaner.desconocido?.(desconocido, () => setDesconocido(null))}
        </div>
      )}

      {abierto && filas > 0 && (
        /* Drawn over the page below the box: a lesson's spotlight on the box takes it in too. */
        <ul
          ref={lista}
          data-guia-desborde
          className="entra absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl bg-slate-800 shadow-2xl shadow-black/60 ring-1 ring-white/10 ring-inset"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (cuantos < encontrados.length && el.scrollTop + el.clientHeight >= el.scrollHeight - 150) setCuantos(cuantos + PRODUCTOS_A_LA_VEZ);
          }}
        >
          {resultados.map((p, i) => (
            <li key={p.id}>
              {/*
                The picture comes first: in a list of three sodas it is the thing that tells them
                apart before the eye has reached "3 litros".
              */}
              <button
                type="button"
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                  i === resaltado ? 'bg-slate-700/60' : ''
                }`}
                // Moved over, not merely under the mouse: the arrows scroll the list, and the row a
                // resting pointer found itself over took the highlight from them.
                onMouseMove={() => setResaltado(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => elegir(p)}
                data-sin-icono="fila"
              >
                <Miniatura producto={p} tamano="sm" />
                {p.activo ? (
                  <span className="min-w-0 flex-1 truncate">{nombre(p)}</span>
                ) : (
                  // Under the name, which keeps the room it has: one taken off sale, found for a count.
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{nombre(p)}</span>
                    <span className="block text-xs text-amber-300/80" data-buscador-desactivado>
                      {t('buscador.desactivado')}
                    </span>
                  </span>
                )}
                {(() => {
                  // What choosing it rings up: the case when the code typed is the case's own.
                  const u = deLaConsulta.find((x) => x.productoId === p.id);
                  return u ? (
                    <span className="shrink-0 text-xs font-medium text-teal-300">{etiquetaUnidad(u.nombre, u.nombre)}</span>
                  ) : (
                    <span className="shrink-0 text-xs text-slate-500">{etiquetaUnidad(p.unidadBase, p.unidadBase)}</span>
                  );
                })()}
              </button>
            </li>
          ))}
          {nuevo && (
            // Kept in sight at the foot of a list long enough to scroll.
            <li className="sticky bottom-0 border-t border-white/5 bg-slate-800">
              <button
                type="button"
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium text-teal-300 ${
                  resaltado >= resultados.length ? 'bg-slate-700/60' : ''
                }`}
                onMouseMove={() => setResaltado(resultados.length)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => crear(nuevo)}
              >
                <Ico icono="nuevo" className="size-5 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{t('buscador.crear', { nombre: nuevo })}</span>
              </button>
            </li>
          )}
        </ul>
      )}

      {pregunta !== null && (
        <Modal titulo={t('buscador.preguntaTitulo')} onCerrar={() => setPregunta(null)}>
          <PreguntaCodigo
            codigo={pregunta}
            puedeEnsenar={puedeEnsenar}
            conPista={Boolean(onNuevo)}
            onEnsenar={() => {
              setPregunta(null);
              setEnsenando(pregunta);
            }}
            onSeguir={() => setPregunta(null)}
            onElegido={(p, unidad) => {
              setPregunta(null);
              onElegir(p, unidad);
            }}
          >
            {escaner !== false && escaner.desconocido?.(pregunta, () => setPregunta(null))}
          </PreguntaCodigo>
        </Modal>
      )}

      {cual && (
        <Modal titulo={t('cualEs.titulo')} onCerrar={soltarCual}>
          <CualEs
            codigo={cual.codigo}
            hallados={cual.hallados}
            {...(listaId ? { listaId } : {})}
            onElegido={({ producto, unidad }) => {
              soltarCual();
              onElegir(producto, unidad);
            }}
            onSeguir={soltarCual}
          />
        </Modal>
      )}

      {ensenando && (
        <Modal titulo={t('escaner.cualProducto')} onCerrar={() => setEnsenando(null)}>
          <p className="mb-4 text-sm text-slate-400">{t('escaner.cualProductoAyuda', { codigo: ensenando })}</p>
          <EnsenarCodigo
            codigo={ensenando}
            onListo={(p, unidad) => {
              setEnsenando(null);
              setDesconocido(null);
              onElegir(p, unidad);
            }}
          />
        </Modal>
      )}

      {escaneando && escaner !== false && (
        <EscanerProductos
          {...escaner}
          // Where unknown codes are asked about in a sheet, the camera's question says what they probably are too.
          {...(preguntarEnHoja
            ? { sugerir: (codigo, listo) => <SugerenciaAbierta codigo={codigo} onElegido={listo} conPista={Boolean(onNuevo)} /> }
            : {})}
          titulo={escaner.titulo ?? t('escaner.titulo')}
          {...(listaId ? { listaId } : {})}
          inactivos={inactivos}
          onProducto={onElegir}
          onCerrar={() => setEscaneando(false)}
        />
      )}
    </div>
  );
}

/**
 * A code nobody has, asked about in a sheet: what the camera asks over its picture, over the
 * screen, with what the open catalogues say it probably is. The cursor goes into the sheet, out of
 * the box behind it, so the next code scanned neither lands there nor presses one of these buttons
 * with its Enter. Productos asks it too, over its list.
 */
export function PreguntaCodigo({
  codigo,
  puedeEnsenar,
  conPista,
  onEnsenar,
  onSeguir,
  onElegido,
  children,
}: {
  codigo: string;
  puedeEnsenar: boolean;
  /**
   * The screen makes products (`onNuevo`), so its «Crear producto» is among the ways out, and the
   * suggestion says it fills the new one in. The till's are a venta rápida, which it does not.
   */
  conPista: boolean;
  onEnsenar: () => void;
  onSeguir: () => void;
  /** One of the shop's products, suggested by the open catalogues, now carrying the code. */
  onElegido: (p: Producto, unidad?: UnidadVenta) => void;
  /** The screen's own ways out: making it a new product, say. */
  children?: ReactNode;
}) {
  const t = useT();
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => raiz.current?.focus(), []);
  return (
    <div ref={raiz} tabIndex={-1} className="flex flex-col gap-4 outline-none">
      <div className="flex items-start gap-3">
        <Chip icono="aviso" tinte="ambar" />
        <p className="min-w-0 self-center font-medium break-words">{t('buscador.codigoDesconocido', { codigo })}</p>
      </div>
      <SugerenciaAbierta codigo={codigo} onElegido={onElegido} conPista={conPista} />
      {puedeEnsenar ? (
        <>
          <p className="text-sm text-slate-400">{t('escaner.noEstaAyuda')}</p>
          <button type="button" className="btn-primario" onClick={onEnsenar}>
            <Ico icono="enlazar" />
            {t('escaner.esDeUnProducto')}
          </button>
        </>
      ) : (
        <p className="text-sm text-slate-400">{t('escaner.pidaAlDueno')}</p>
      )}
      {children}
      <button type="button" className="btn-secundario" onClick={onSeguir}>
        <Ico icono="escanear" />
        {t('escaner.seguir')}
      </button>
    </div>
  );
}
